/**
 * Sidebar structure (blueprint §9.1, ui-architecture.html). Every page of the v1 app stays
 * reachable under the "V1" group at the bottom, exactly as it was — nothing removed.
 */
import type { IconName } from "./components/Icon";

export interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** key into the /nav-counts response, if the item shows a count */
  count?: string;
  hot?: boolean;
  /** other words people search for it by (Search or jump to), e.g. a former name */
  keywords?: string;
}

export interface NavModule {
  key: string;
  label: string;
  railLabel: string;
  railIcon: IconName;
  groups: Array<{ heading?: string; items: NavItem[] }>;
}

export const MODULES: NavModule[] = [
  {
    key: "ads",
    label: "Google Ads",
    railLabel: "Ads",
    railIcon: "bars",
    groups: [
      {
        items: [
          { to: "/", label: "Home", icon: "home" },
          {
            to: "/analytics",
            label: "Analytics",
            icon: "activity",
            keywords: "shopify traffic sessions sales",
          },
          {
            to: "/inbox",
            label: "Suggestions",
            icon: "inbox",
            count: "inbox",
            hot: true,
            keywords: "inbox approvals",
          },
        ],
      },
      {
        heading: "Advertise",
        items: [
          { to: "/campaigns", label: "Campaigns", icon: "layers", count: "campaigns" },
          { to: "/products", label: "Products", icon: "box", count: "products" },
          { to: "/search-terms", label: "Search terms", icon: "search", count: "searchTerms" },
          { to: "/keywords", label: "Keyword Lab", icon: "key", count: "keywords" },
          { to: "/ads", label: "Ad Studio", icon: "type" },
          { to: "/measurement", label: "Measurement", icon: "activity" },
        ],
      },
      {
        heading: "Control",
        items: [
          { to: "/automations", label: "Automations", icon: "zap", count: "automations" },
          { to: "/profit", label: "Profit model", icon: "coins" },
          { to: "/changes", label: "Changes", icon: "git", count: "drafts" },
          { to: "/reports", label: "Reports", icon: "bars" },
        ],
      },
    ],
  },
  {
    key: "catalog",
    label: "Catalog & SEO",
    railLabel: "Catalog",
    railIcon: "tag",
    groups: [
      {
        items: [
          { to: "/growth", label: "Growth", icon: "trend" },
          { to: "/catalog", label: "Catalog", icon: "box" },
          { to: "/seo", label: "Product SEO", icon: "search" },
          { to: "/collections", label: "Collections", icon: "layers" },
        ],
      },
    ],
  },
  {
    key: "social",
    label: "Viktoriia",
    railLabel: "Viktoriia",
    railIcon: "camera",
    groups: [
      {
        items: [
          { to: "/vk/chat", label: "Chat", icon: "msg" },
          { to: "/vk/calendar", label: "Calendar", icon: "calendar" },
          { to: "/vk/meta", label: "Meta Ads", icon: "megaphone" },
          { to: "/vk/email", label: "Email", icon: "mail" },
          { to: "/vk/creative", label: "Creative studio", icon: "image" },
          { to: "/vk/giveaways", label: "Giveaways", icon: "gift" },
          { to: "/vk/analytics", label: "Analytics", icon: "bars" },
          { to: "/vk/work", label: "Scheduled work", icon: "clock" },
        ],
      },
    ],
  },
  {
    key: "team",
    label: "Team",
    railLabel: "Team",
    railIcon: "users",
    groups: [{ items: [{ to: "/team", label: "Tasks", icon: "check", count: "tasks", hot: true }] }],
  },
  {
    key: "admin",
    label: "Admin",
    railLabel: "Admin",
    railIcon: "gear",
    groups: [
      {
        items: [
          { to: "/brand", label: "Brand brain", icon: "book" },
          { to: "/settings", label: "Settings", icon: "gear" },
          { to: "/ai-usage", label: "AI Usage", icon: "spark" },
          {
            to: "/integrations",
            label: "API keys",
            icon: "key",
            keywords: "api feed seo app integration token",
          },
          { to: "/system", label: "System status", icon: "pulse" },
          { to: "/onboarding", label: "Onboarding", icon: "flag" },
        ],
      },
    ],
  },
];

/** The v1 app's menu, unchanged, shown nested under "V1" (links open the v1 app). */
export const V1_GROUPS: Array<{
  heading?: string;
  items: Array<{ path: string; label: string; icon: IconName }>;
}> = [
  {
    items: [
      { path: "/", label: "Dashboard", icon: "grid" },
      { path: "/products", label: "Products", icon: "box" },
      { path: "/keywords", label: "Keyword engine", icon: "search" },
      { path: "/rules", label: "Rules", icon: "shield" },
      { path: "/profit", label: "Profit & costs", icon: "coins" },
    ],
  },
  {
    heading: "Admin",
    items: [
      { path: "/connections", label: "Connections", icon: "link" },
      { path: "/other-app", label: "Other app", icon: "msg" },
      { path: "/settings", label: "Settings", icon: "gear" },
      { path: "/users", label: "Users", icon: "users" },
      { path: "/status", label: "System status", icon: "activity" },
    ],
  },
];

export function moduleFor(pathname: string): NavModule {
  return (
    MODULES.find((m) =>
      m.groups.some((g) =>
        g.items.some((i) => i.to !== "/" && (pathname === i.to || pathname.startsWith(`${i.to}/`))),
      ),
    ) ?? MODULES[0]!
  );
}
